import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Building2, MapPin, ArrowRight, ArrowLeft, Check, Loader2, Upload, Sparkles, Layers,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEPS = [
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'properties', label: 'Properties', icon: MapPin },
  { id: 'attributes', label: 'Attributes', icon: Layers },
  { id: 'finish', label: 'Launch', icon: Sparkles },
];

const SERVICE_LEVELS = ['economy', 'select', 'full', 'luxury'];
const ANCILLARY_OPS = [
  { value: 'fb', label: 'Food & Beverage' },
  { value: 'meetings', label: 'Meeting Space' },
  { value: 'spa', label: 'Spa & Wellness' },
  { value: 'retail', label: 'Retail' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'valet', label: 'Valet / Parking' },
];

export default function OnboardingWizard({ token, onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Step 1: Company
  const [companyName, setCompanyName] = useState('');

  // Step 2: Properties
  const [properties, setProperties] = useState([{ name: '', type: 'hotel', service_level: 'select', rooms: '', ancillary: [] }]);

  // Step 3: Attributes filled per property (simplified inline)
  const [tenantId, setTenantId] = useState('');

  const addProperty = () => setProperties(p => [...p, { name: '', type: 'hotel', service_level: 'select', rooms: '', ancillary: [] }]);
  const updateProp = (idx, field, val) => setProperties(p => p.map((pr, i) => i === idx ? { ...pr, [field]: val } : pr));
  const removeProp = (idx) => setProperties(p => p.filter((_, i) => i !== idx));
  const toggleAncillary = (idx, op) => {
    setProperties(p => p.map((pr, i) => i !== idx ? pr : {
      ...pr, ancillary: pr.ancillary.includes(op) ? pr.ancillary.filter(a => a !== op) : [...pr.ancillary, op]
    }));
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      // 1. Create tenant
      const tenantRes = await axios.post(`${BACKEND_URL}/api/admin/tenants`, { name: companyName }, { headers });
      const tid = tenantRes.data.id;
      setTenantId(tid);

      // 2. Create properties with attributes
      for (const prop of properties) {
        if (!prop.name.trim()) continue;
        const slug = prop.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        await axios.post(`${BACKEND_URL}/api/admin/properties`, {
          name: prop.name, slug, type: prop.type, tenant_id: tid,
        }, { headers });
        // Set attributes
        await axios.put(`${BACKEND_URL}/api/admin/properties/${slug}/attributes`, {
          property_type: prop.type,
          service_level: prop.service_level || '',
          total_guestrooms: parseInt(prop.rooms) || 0,
          ancillary_operations: prop.ancillary,
        }, { headers });
      }

      toast.success(`${companyName} created with ${properties.filter(p => p.name).length} properties!`);
      if (onComplete) onComplete(tid);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed');
    } finally {
      setSaving(false);
    }
  };

  const canProceed = step === 0 ? companyName.trim() : step === 1 ? properties.some(p => p.name.trim()) : true;

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="onboarding-wizard">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          const done = i < step;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && <div className={`flex-1 h-0.5 ${done ? 'bg-primary' : 'bg-muted'}`} />}
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${active ? 'bg-primary text-primary-foreground' : done ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                {done ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 1: Company */}
      {step === 0 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Welcome to LodgiFlow</h2>
            <p className="text-muted-foreground mt-1">Let's set up your management company workspace.</p>
          </div>
          <div>
            <Label>Company Name</Label>
            <Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="e.g., Coastal Hospitality Group" className="mt-1" autoFocus data-testid="company-name-input" />
          </div>
        </div>
      )}

      {/* Step 2: Properties */}
      {step === 1 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Add Your Properties</h2>
            <p className="text-muted-foreground mt-1">Add the properties in your portfolio. You can add more later.</p>
          </div>
          <div className="space-y-4">
            {properties.map((prop, i) => (
              <div key={i} className="p-4 border rounded-xl space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Property Name</Label>
                    <Input value={prop.name} onChange={e => updateProp(i, 'name', e.target.value)} placeholder="e.g., Downtown Hotel" />
                  </div>
                  <div className="w-36">
                    <Label className="text-xs">Type</Label>
                    <Select value={prop.type} onValueChange={v => updateProp(i, 'type', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hotel">Hotel</SelectItem>
                        <SelectItem value="restaurant">Restaurant</SelectItem>
                        <SelectItem value="entertainment">Entertainment</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {properties.length > 1 && (
                    <Button variant="ghost" size="sm" className="mt-5 text-destructive" onClick={() => removeProp(i)}>Remove</Button>
                  )}
                </div>
                {prop.type === 'hotel' && (
                  <div className="flex gap-3">
                    <div className="w-36">
                      <Label className="text-xs">Service Level</Label>
                      <Select value={prop.service_level} onValueChange={v => updateProp(i, 'service_level', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SERVICE_LEVELS.map(l => <SelectItem key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-28">
                      <Label className="text-xs">Rooms</Label>
                      <Input type="number" value={prop.rooms} onChange={e => updateProp(i, 'rooms', e.target.value)} placeholder="150" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Operations</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {ANCILLARY_OPS.map(op => (
                          <button key={op.value} onClick={() => toggleAncillary(i, op.value)}
                            className={`px-2 py-1 rounded-md text-[10px] font-medium border ${prop.ancillary.includes(op.value) ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-muted/40 border-transparent text-muted-foreground'}`}
                          >{op.label}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Button variant="outline" onClick={addProperty} className="w-full gap-1.5"><MapPin className="h-3.5 w-3.5" /> Add Another Property</Button>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 2 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Review & Launch</h2>
            <p className="text-muted-foreground mt-1">Here's what we'll set up for you.</p>
          </div>
          <div className="p-4 border rounded-xl space-y-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="font-semibold">{companyName}</span>
            </div>
            <Separator />
            {properties.filter(p => p.name).map((p, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">{p.name}</span>
                <Badge variant="outline" className="text-[10px]">{p.type}</Badge>
                {p.type === 'hotel' && <Badge variant="secondary" className="text-[10px]">{p.service_level} · {p.rooms || '?'} rooms</Badge>}
                {p.ancillary.length > 0 && <span className="text-xs text-muted-foreground">{p.ancillary.join(', ')}</span>}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Platform templates will be auto-generated based on each property's type and attributes. You can customize everything after setup.</p>
        </div>
      )}

      {/* Step 4: Complete */}
      {step === 3 && (
        <div className="text-center py-12">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>You're all set!</h2>
          <p className="text-muted-foreground mb-6">Your workspace is ready. Start building your tech stack.</p>
          <Button onClick={() => onComplete?.(tenantId)} className="gap-1.5">
            Go to Dashboard <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Navigation */}
      {step < 3 && (
        <div className="flex items-center justify-between mt-8 pt-6 border-t">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(s => s - 1)} className="gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Button>
          {step < 2 ? (
            <Button disabled={!canProceed} onClick={() => setStep(s => s + 1)} className="gap-1.5">
              Continue <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button disabled={saving} onClick={handleFinish} className="gap-1.5" data-testid="launch-workspace">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {saving ? 'Creating...' : 'Launch Workspace'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
